/**
 * @file HTTP-операции со списком файлов, переименованием и удалением.
 *
 * Пути кодируются в base64 для передачи Unicode в заголовках. Успешный ответ
 * с идентификатором задачи ещё не означает завершение: ожидаем явный done.
 */
import {
    FileOperationError,
    checkFileResponse,
    parseFolderContent,
    validateRenameName,
} from '../features/files/model.ts'
import type { FolderContent } from '../features/files/model.ts'
import { waitForFileTask } from '../features/files/task.ts'
import { b64Encode } from './encoding.ts'
import { makeTunnelRequest } from './request.ts'
import { isTaskId } from './taskId.ts'

export type { FolderContent } from '../features/files/model.ts'

type Access = { boxID: string; token: string; signal?: AbortSignal }

export async function renameFile({
    filePath,
    newFilePath,
    boxID,
    token,
    signal,
}: Access & { filePath: string; newFilePath: string }): Promise<void> {
    const name = newFilePath.slice(newFilePath.lastIndexOf('/') + 1)
    const error = validateRenameName(name, '', new Set())
    if (error) throw new FileOperationError(error)

    if (
        filePath.slice(0, filePath.lastIndexOf('/')) !==
        newFilePath.slice(0, newFilePath.lastIndexOf('/'))
    )
        throw new FileOperationError('invalidName')

    const bounded = AbortSignal.any([
        ...(signal ? [signal] : []),
        AbortSignal.timeout(5 * 60_000),
    ])
    bounded.throwIfAborted()
    const response = await makeTunnelRequest({
        path: '/file',
        boxID,
        token,
        opts: {
            method: 'PATCH',
            signal: bounded,
            headers: {
                'x-file-path': b64Encode(filePath),
                'x-new-file-path': b64Encode(newFilePath),
            },
        },
    })
    checkFileResponse(response)
    await confirmTask(
        response,
        { boxID, token },
        bounded,
        response.status === 202,
    )
}

// Одни серверные операции завершаются сразу, другие возвращают X-Task-ID.
// required запрещает считать принятый, но неподтверждённый запрос успехом.
async function confirmTask(
    response: Response,
    access: Access,
    signal: AbortSignal,
    required: boolean,
): Promise<void> {
    const id = response.headers.get('X-Task-ID')
    if (!isTaskId(id)) {
        if (required) throw new FileOperationError('unconfirmed')

        return
    }

    await waitForFileTask(
        (taskSignal) =>
            makeTunnelRequest({
                path: `/task/${id}`,
                boxID: access.boxID,
                token: access.token,
                opts: { signal: taskSignal, cache: 'no-store' },
            }),
        signal,
    )
}

async function remove(
    kind: 'file' | 'folder',
    path: string,
    access: Access,
): Promise<void> {
    const signal = AbortSignal.any([
        ...(access.signal ? [access.signal] : []),
        AbortSignal.timeout(5 * 60_000),
    ])
    signal.throwIfAborted()
    const response = await makeTunnelRequest({
        path: `/${kind}`,
        boxID: access.boxID,
        token: access.token,
        opts: {
            method: 'DELETE',
            signal,
            headers: { [`x-${kind}-path`]: b64Encode(path) },
        },
    })
    checkFileResponse(response)
    await confirmTask(response, access, signal, true)
}

export function deleteFile({
    filePath,
    ...access
}: Access & { filePath: string }): Promise<void> {
    return remove('file', filePath, access)
}

export function deleteFolder({
    folderPath,
    ...access
}: Access & { folderPath: string }): Promise<void> {
    return remove('folder', folderPath, access)
}

export async function fetchFolderContent({
    currentFolder,
    boxID,
    token,
    signal,
}: Access & { currentFolder: string }): Promise<FolderContent> {
    const response = await makeTunnelRequest({
        path: '/folder',
        boxID,
        token,
        opts: {
            signal,
            cache: 'no-store',
            headers: { 'x-folder-path': b64Encode(currentFolder) },
        },
    })
    checkFileResponse(response)
    const content = parseFolderContent(await response.json())
    signal?.throwIfAborted()

    return content
}
